import React, { useState, useRef, KeyboardEvent, useEffect, useCallback } from 'react';
import {
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popper,
  styled,
  InputAdornment,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Code as CodeIcon,
  Image as ImageIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { Agent } from '../../stores/chatStore';
import AgentAvatar from './AgentAvatar';
import QuickActionBar from './QuickActionBar';
import { AgentService } from '../../services/api/agent';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import BatchUploadDialog from './BatchUploadDialog';

export interface MentionableUser {
  id: string;
  name: string;
  email?: string;
  type: 'human';
}

interface MessageInputProps {
  onSendMessage: (content: string, agentId?: string) => void;
  agents: Agent[];
  disabled?: boolean;
  placeholder?: string;
  conversationId?: string;
  participants?: MentionableUser[];
  conversationType?: 'ai_chat' | 'dm' | 'group' | 'channel';
}

const InputContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0, // Prevent the input from shrinking
  position: 'relative',
  zIndex: 1,
  // iPad keyboard fix
  paddingBottom: `max(${theme.spacing(1)}, env(safe-area-inset-bottom))`,
  '@media (max-width: 768px)': {
    // Additional mobile adjustments
    paddingBottom: `max(${theme.spacing(2)}, env(safe-area-inset-bottom))`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.default,
  },
}));

const MentionList = styled(List)(({ theme }) => ({
  padding: 0,
  maxHeight: 200,
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius,
}));

// Tool definitions for slash commands
const AVAILABLE_TOOLS = [
  {
    name: 'search_emails',
    description: 'Search through your email history',
    usage: '/search_emails [query]',
    icon: 'mail',
  },
  {
    name: 'knowledge_graph_query',
    description: 'Query the knowledge graph',
    usage: '/knowledge_graph_query [query]',
    icon: 'hub',
  },
  {
    name: 'delegate_to_agent',
    description: 'Delegate a task to another agent',
    usage: '/delegate_to_agent [agent_name] [task]',
    icon: 'group',
  },
  {
    name: 'list_available_agents',
    description: 'List all available AI agents',
    usage: '/list_available_agents',
    icon: 'list',
  },
  {
    name: 'get_agent_details',
    description: 'Get details about a specific agent',
    usage: '/get_agent_details [agent_name]',
    icon: 'info',
  },
  {
    name: 'start_agent_interview',
    description: 'Start creating a custom agent',
    usage: '/start_agent_interview',
    icon: 'mic',
  },
  {
    name: 'get_last_email',
    description: 'Get your most recent email',
    usage: '/get_last_email',
    icon: 'inbox',
  },
  {
    name: 'email_context_search',
    description: 'Search emails for context',
    usage: '/email_context_search [query]',
    icon: 'search',
  },
];

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  agents = [],
  disabled = false,
  placeholder = "Type a message... Use @ to mention teammates, / for tools",
  conversationId,
  participants = [],
  conversationType = 'ai_chat',
}) => {
  const [message, setMessage] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [showToolHints, setShowToolHints] = useState(false);
  const [toolSearch, setToolSearch] = useState('');
  const [toolAnchor, setToolAnchor] = useState<HTMLElement | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [uploading, setUploading] = useState(false);
  const [, setUploadProgress] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [fileMenuAnchor, setFileMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachButtonRef = useRef<HTMLButtonElement>(null);
  const agentService = new AgentService();
  const featureFlags = useFeatureFlags();
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);

  // iPad keyboard fix - handle viewport changes
  useEffect(() => {
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const viewport = window.visualViewport;
        document.documentElement.style.setProperty('--viewport-height', `${viewport.height}px`);

        // If input is focused and viewport height changes (keyboard appears)
        if (inputRef.current === document.activeElement) {
          setTimeout(() => {
            inputRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
              inline: 'nearest'
            });
          }, 150);
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    }
  }, []);

  // Handle input focus on mobile
  const handleInputFocus = useCallback(() => {
    // Small delay to allow keyboard to appear
    setTimeout(() => {
      if (inputRef.current && window.innerWidth <= 768) {
        inputRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }
    }, 300);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessage(value);

    // Check for slash commands
    const lastSlashIndex = value.lastIndexOf('/');
    const lastSpaceIndex = value.lastIndexOf(' ');
    
    // Show tool hints if "/" is at the start or after a space
    if (lastSlashIndex !== -1 && (lastSlashIndex === 0 || lastSpaceIndex === lastSlashIndex - 1)) {
      const textAfterSlash = value.substring(lastSlashIndex + 1);
      const hasSpaceAfterSlash = textAfterSlash.includes(' ');
      
      if (!hasSpaceAfterSlash) {
        setToolSearch(textAfterSlash.toLowerCase());
        setShowToolHints(true);
        setToolAnchor(e.target);
        setShowMentions(false); // Hide mentions when showing tools
      } else {
        setShowToolHints(false);
      }
    } else {
      setShowToolHints(false);
    }

    // Check for @ mentions (but not when showing tool hints)
    if (!showToolHints) {
      const lastAtIndex = value.lastIndexOf('@');
      if (lastAtIndex !== -1) {
        const textAfterAt = value.substring(lastAtIndex + 1);
        const hasSpaceAfterAt = textAfterAt.includes(' ');
        
        if (!hasSpaceAfterAt) {
          setMentionSearch(textAfterAt.toLowerCase());
          setShowMentions(true);
          setMentionAnchor(e.target);
        } else {
          setShowMentions(false);
        }
      } else {
        setShowMentions(false);
      }
    }
  };

  const handleSelectMention = (target: Agent | MentionableUser | 'all') => {
    const lastAtIndex = message.lastIndexOf('@');
    const beforeAt = message.substring(0, lastAtIndex);
    const afterMention = message.substring(lastAtIndex + 1 + mentionSearch.length);

    if (target === 'all') {
      setMessage(`${beforeAt}@all ${afterMention}`);
      setSelectedAgent(null);
    } else if ('type' in target && target.type === 'human') {
      const mentionName = target.name;
      setMessage(`${beforeAt}@${mentionName} ${afterMention}`);
      setSelectedAgent(null);
    } else {
      const agent = target as Agent;
      const mentionName = agent.custom_name || agent.name;
      setMessage(`${beforeAt}@${mentionName} ${afterMention}`);
      setSelectedAgent(agent);
    }

    setShowMentions(false);
    inputRef.current?.focus();
  };

  const handleSelectTool = (tool: typeof AVAILABLE_TOOLS[0]) => {
    const lastSlashIndex = message.lastIndexOf('/');
    const beforeSlash = message.substring(0, lastSlashIndex);
    const afterTool = message.substring(lastSlashIndex + 1 + toolSearch.length);
    
    // Insert the tool name with a space after it
    setMessage(`${beforeSlash}/${tool.name} ${afterTool}`);
    
    setShowToolHints(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    
    if (e.key === 'Escape') {
      if (showMentions) {
        setShowMentions(false);
      }
      if (showToolHints) {
        setShowToolHints(false);
      }
    }
  };

  const handleSend = () => {
    if (message.trim()) {
      // Only send to a specific agent if one is explicitly selected
      const agentId = selectedAgent?.id;

      onSendMessage(message.trim(), agentId);
      setMessage('');
      setSelectedAgent(null);
    }
  };

  const handleInsertText = (text: string) => {
    const currentValue = message;
    const cursorPosition = inputRef.current?.selectionStart || currentValue.length;

    const newValue =
      currentValue.slice(0, cursorPosition) +
      text +
      currentValue.slice(cursorPosition);

    setMessage(newValue);

    // Focus the input and set cursor position
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newCursorPosition = cursorPosition + text.length;
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const filteredAgents = agents.filter(agent => {
    const name = (agent.custom_name || agent.name).toLowerCase();
    return name.includes(mentionSearch) && agent.is_active;
  });

  const filteredParticipants = participants.filter(p =>
    p.name.toLowerCase().includes(mentionSearch)
  );

  const isMultiParticipant = conversationType !== 'ai_chat';
  const showAllOption = isMultiParticipant && 'all'.includes(mentionSearch);

  const filteredTools = AVAILABLE_TOOLS.filter(tool => {
    return tool.name.toLowerCase().includes(toolSearch) || 
           tool.description.toLowerCase().includes(toolSearch);
  });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleFileSelect called');
    console.log('Event:', event);
    console.log('Files:', event.target.files);
    
    const file = event.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name);

    // Check file size
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setNotification({
        open: true,
        message: 'File size must be less than 100MB',
        severity: 'error',
      });
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Accept all file types - no restrictions
    // Store the file and show provider selection menu
    setSelectedFile(file);
    
    // Use a timeout to ensure state is updated before setting the menu anchor
    setTimeout(() => {
      console.log('Setting file menu anchor after delay, attachButtonRef:', attachButtonRef.current);
      if (attachButtonRef.current) {
        setFileMenuAnchor(attachButtonRef.current);
      } else {
        console.error('attachButtonRef.current is null - cannot show menu');
        // Try to open menu at event target instead
        const target = event.target as HTMLElement;
        if (target) {
          setFileMenuAnchor(target);
        }
      }
    }, 0);
  };



  const handleProviderSelect = async (provider: 'personal' | 'organization') => {
    setFileMenuAnchor(null);
    
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload the document with scope information
        try {
          await agentService.uploadDocument(selectedFile, conversationId, (progress) => setUploadProgress(progress), {
            scope: provider // 'personal' or 'organization'
          });

          // Send a message about the uploaded document with scope info
          const scopeText = provider === 'personal' ? 'personal knowledge graph' : 'organization-wide knowledge graph';
          const uploadMessage = `I've uploaded and processed "${selectedFile.name}" for the ${scopeText}. The content is now available for our conversation context.`;
          onSendMessage(uploadMessage);

          setNotification({
            open: true,
            message: `Document processed and added to ${scopeText}!`,
            severity: 'success',
          });
        } catch (error: any) {
          // Handle error similar to knowledge-only upload
          if (error?.response?.status === 409 && error?.response?.data?.error === 'duplicate_file') {
            const duplicateData = error.response.data;

            // Show confirmation dialog
            const userConfirmed = window.confirm(
              `${duplicateData.message}\n\n` +
              `Last processed: ${new Date(duplicateData.existingFile.lastProcessed).toLocaleString()}\n\n` +
              `${duplicateData.prompt}`
            );

            if (userConfirmed) {
              // Reprocess the file with scope
              try {
                const formData = new FormData();
                formData.append('file', selectedFile);
                if (conversationId) {
                  formData.append('conversationId', conversationId);
                }
                formData.append('existingFileId', duplicateData.existingFile.id);
                formData.append('projectId', duplicateData.existingFile.projectId);
                formData.append('scope', provider); // Add scope to reprocessing

                const response = await fetch(window.location.origin + '/api/v1/documents/reprocess-cad', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  },
                  body: formData,
                });

                if (response.ok) {
                  await response.json();
                  const scopeText = provider === 'personal' ? 'personal knowledge graph' : 'organization-wide knowledge graph';
                  onSendMessage(`I've reprocessed the CAD file "${selectedFile.name}" for the ${scopeText} with the latest extraction logic. The updated data is now available.`);

                  setNotification({
                    open: true,
                    message: 'CAD file reprocessed successfully!',
                    severity: 'success',
                  });
                } else {
                  throw new Error('Failed to reprocess CAD file');
                }
              } catch (reprocessError) {
                console.error('Reprocessing failed:', reprocessError);
                setNotification({
                  open: true,
                  message: 'Failed to reprocess CAD file',
                  severity: 'error',
                });
              }
            } else {
              setNotification({
                open: true,
                message: 'Upload cancelled - file already exists',
                severity: 'info',
              });
            }
          } else {
            throw error;
          }
        }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({
        open: true,
        message: 'Failed to upload document. Please try again.',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getAvailableProviders = () => {
    // Always show personal and organization options regardless of integrations
    return ['personal', 'organization'];
  };

  return (
    <InputContainer elevation={0}>
      <Stack spacing={1}>
        {/* Quick Action Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {selectedAgent && (
              <Chip
                avatar={<AgentAvatar agent={selectedAgent} size={24} />}
                label={`Asking ${selectedAgent.custom_name || selectedAgent.name}`}
                onDelete={() => setSelectedAgent(null)}
                size="small"
              />
            )}
          </div>
          <QuickActionBar
            onSendMessage={onSendMessage}
            onInsertText={handleInsertText}
            agents={agents}
            conversationId={conversationId}
            disabled={disabled}
          />
        </div>
        
        <StyledTextField
          ref={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" spacing={0.5}>
                  <label htmlFor="file-upload-input">
                    <input
                      id="file-upload-input"
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                      disabled={disabled || uploading}
                    />
                    <IconButton 
                      ref={attachButtonRef}
                      component="span"
                      size="small" 
                      disabled={disabled || uploading}
                      onClick={() => console.log('IconButton clicked - label should trigger file input')}
                    >
                      {uploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AttachFileIcon fontSize="small" />
                      )}
                    </IconButton>
                  </label>
                  {featureFlags.batchUpload && (
                    <Tooltip title="Batch upload documents to knowledge graph">
                      <IconButton
                        size="small"
                        onClick={() => setBatchUploadOpen(true)}
                        disabled={disabled || uploading}
                      >
                        <CloudUploadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton size="small" disabled>
                    <ImageIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled>
                    <CodeIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleSend}
                    disabled={!message.trim() || disabled}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Mention suggestions */}
      <Popper open={showMentions} anchorEl={mentionAnchor} placement="top-start">
        <MentionList>
          {showAllOption && (
            <ListItem button onClick={() => handleSelectMention('all')}>
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>@</Avatar>
              </ListItemAvatar>
              <ListItemText primary="@all" secondary="Notify all members in this conversation" />
            </ListItem>
          )}
          {filteredParticipants.length > 0 && isMultiParticipant && (
            <>
              <ListItem sx={{ py: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Members
                </Typography>
              </ListItem>
              {filteredParticipants.map((p) => (
                <ListItem key={p.id} button onClick={() => handleSelectMention(p)}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
                      {p.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={p.name} secondary={p.email || 'Member'} />
                </ListItem>
              ))}
            </>
          )}
          {filteredAgents.length > 0 && (
            <>
              {isMultiParticipant && (
                <ListItem sx={{ py: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    AI Teammates
                  </Typography>
                </ListItem>
              )}
              {filteredAgents.map((agent) => (
                <ListItem key={agent.id} button onClick={() => handleSelectMention(agent)}>
                  <ListItemAvatar>
                    <AgentAvatar agent={agent} size={32} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={agent.custom_name || agent.name}
                    secondary={isMultiParticipant ? 'AI Teammate' : agent.type}
                  />
                </ListItem>
              ))}
            </>
          )}
          {filteredAgents.length === 0 && filteredParticipants.length === 0 && !showAllOption && (
            <ListItem>
              <ListItemText primary="No matches found" />
            </ListItem>
          )}
        </MentionList>
      </Popper>

      {/* Tool hints */}
      <Popper open={showToolHints} anchorEl={toolAnchor} placement="top-start">
        <Paper
          sx={{
            p: 1,
            maxHeight: 300,
            overflowY: 'auto',
            minWidth: 350,
            boxShadow: 4,
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Available Tools - Type to filter
          </Typography>
          <List dense>
            {filteredTools.map((tool) => (
              <ListItem 
                key={tool.name} 
                button 
                onClick={() => handleSelectTool(tool)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '1.2rem', bgcolor: 'primary.main' }}>
                    {tool.icon}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight="medium">
                        /{tool.name}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {tool.description}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                        {tool.usage}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItem>
            ))}
            {filteredTools.length === 0 && (
              <ListItem>
                <ListItemText 
                  primary="No matching tools found" 
                  secondary="Try a different search term"
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Popper>

      {/* Upload progress/notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Provider Selection Menu - Always render for proper state management */}
      <Menu
        anchorEl={fileMenuAnchor}
        open={Boolean(fileMenuAnchor && selectedFile)}
        onClose={() => {
          console.log('Closing file menu');
          setFileMenuAnchor(null);
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            How would you like to process this document?
          </Typography>
        </MenuItem>
        <Divider />
        {getAvailableProviders().includes('personal') && (
          <MenuItem onClick={() => handleProviderSelect('personal')}>
            <Avatar sx={{ mr: 1, width: 20, height: 20, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
              P
            </Avatar>
            Personal
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (Visible to you only)
            </Typography>
          </MenuItem>
        )}
        {getAvailableProviders().includes('organization') && (
          <MenuItem onClick={() => handleProviderSelect('organization')}>
            <Avatar sx={{ mr: 1, width: 20, height: 20, fontSize: '0.75rem', bgcolor: 'info.main' }}>
              O
            </Avatar>
            Organization Wide
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (Visible to your organization)
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Batch Upload Dialog */}
      <BatchUploadDialog
        open={batchUploadOpen}
        onClose={() => setBatchUploadOpen(false)}
        conversationId={conversationId}
      />
    </InputContainer>
  );
};

export default MessageInput;